import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

// V2 Components
import { DashboardLayout } from '../layouts';
import { AccentButton } from '../components/Button';
import PopupContainer from '../components/PopupContainer';
import Input from '../components/Input';
import Select from '../components/Select';
import Folder from '../components/Folder';
import { DeleteFolderConfirmation } from '../components/ConfirmationPopup';

// Hooks
import { useFolders } from '../../hooks/useFolder';

// Icons
import { 
  FilterIcon,
  ChevronDownIcon
} from '../icons';

import { RETENTION_OPTIONS, DEFAULT_RETENTION } from '../../utils/retention';

const EditFolderModal = ({ folder, onClose, onSave }) => {
  const [name, setName] = useState(folder.name);
  const [retention, setRetention] = useState(folder.retention_period ?? DEFAULT_RETENTION);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  return (
    <div className="w-[320px] bg-white dark:bg-[#121212] border border-[#ECECEC] dark:border-[#202020] rounded-[32px] shadow-[0_26px_38px_0_rgba(18,18,18,0.05)] flex flex-col mx-4">
      <div className="flex flex-col px-6 pt-6 pb-5 gap-4">
        <h2 className="font-medium text-xl leading-7 tracking-tight text-[#121212] dark:text-[#D0D0D0]">
          Edit Folder
        </h2>
        {/* Name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-[#7B7B7B] dark:text-[#848484]">Name</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2 text-sm font-medium rounded-xl border border-[#ECECEC] dark:border-[#202020] bg-white dark:bg-[#1A1A1A] text-[#121212] dark:text-[#D0D0D0] focus:outline-none focus:border-[#121212] dark:focus:border-[#D0D0D0]"
          />
        </div>
        {/* Retention */}
        <Select
          label="Retention"
          options={RETENTION_OPTIONS}
          value={retention}
          onChange={v => { setRetention(v); setError(null); }}
          error={error}
        />
      </div>
      <div className="flex items-center justify-between px-6 py-5 bg-[#FCFCFC] dark:bg-[#1A1A1A] border-t border-[#ECECEC] dark:border-[#202020] rounded-b-[32px]">
        <AccentButton variant="secondary" className="w-[130px]" onClick={onClose}>
          Cancel
        </AccentButton>
        <AccentButton
          variant="primary"
          className="w-[130px]"
          disabled={isSaving}
          onClick={async () => {
            setError(null);
            setIsSaving(true);
            try {
              await onSave({ name: name.trim() || folder.name, retentionPeriod: retention });
            } catch (err) {
              setError(err?.response?.data?.error?.message || 'Failed to update folder');
            } finally {
              setIsSaving(false);
            }
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </AccentButton>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createRetention, setCreateRetention] = useState(DEFAULT_RETENTION);
  const [createRetentionError, setCreateRetentionError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
  const [deleteFolderId, setDeleteFolderId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editRetentionFolderId, setEditRetentionFolderId] = useState(null);
  const navigate = useNavigate();

  const { folders, isLoading, createFolder, isCreating, deleteFolder, updateFolder } = useFolders();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const handleCreateClipboard = async (data) => {
    setCreateRetentionError(null);
    try {
      await createFolder({ name: data.name, retentionPeriod: createRetention });
      setShowCreateModal(false);
      setCreateRetention(DEFAULT_RETENTION);
      setCreateRetentionError(null);
      reset();
    } catch (err) {
      const msg = err?.response?.data?.error?.message;
      if (msg) setCreateRetentionError(msg);
      // toast already fired by mutation's onError
    }
  };

  const handleDeleteClipboard = (clipboardId) => {
    setDeleteError(null);
    setDeleteFolderId(clipboardId);
  };

  const handleMenuItemClick = (item) => {
    setActiveMenuItem(item.id);
  };

  const handleFolderClick = async (clipboardId) => {
    console.log('Folder clicked:', clipboardId);
    navigate(`/my/folder/${clipboardId}`);
  };

  // Layout callback handlers
  const handleBackClick = () => {
    // Handle back navigation
    console.log('Back clicked');
  };

  const handleForwardClick = () => {
    // Handle forward navigation
    console.log('Forward clicked');
  };

  const handleProfileClick = () => {
    // Handle profile click
    console.log('Profile clicked');
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Paste handling moved to Folder page

  // Paste handling moved to Folder page

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-[#121212]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#121212] dark:border-[#D0D0D0]"></div>
      </div>
    );
  }

  return (
    <DashboardLayout
      activeMenuItem={activeMenuItem}
      onMenuItemClick={handleMenuItemClick}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      onBackClick={handleBackClick}
      onForwardClick={handleForwardClick}
      onProfileClick={handleProfileClick}
    >
          {/* My Folders Section */}
          <div className="border-b border-[#ECECEC] dark:border-[#202020] bg-white dark:bg-[#121212]">
            <div className="h-[70px] flex items-center justify-between px-6">
              <h2 className="text-xl font-medium text-[#121212] dark:text-[#D0D0D0]">Folders</h2>
              
              <div className="flex items-center gap-4">
                {/* Sort Dropdown */}
                <div className="hidden flex items-center gap-2 px-3 py-2 bg-[#F1F1F1] dark:bg-[#2A2A2A] rounded-xl">
                  <FilterIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
                  <span className="text-sm font-medium text-[#121212] dark:text-[#D0D0D0]">Newest first</span>
                  <ChevronDownIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
                </div>

                {/* Create New Button */}
                <AccentButton 
                  variant="secondary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create New
                </AccentButton>
              </div>
            </div>
          </div>

          {/* Folders Grid */}
          {/* <div className="p-6">
            <div className="grid grid-cols-4 gap-6">
              {folders.map((clipboard) => (
                <Folder
                    key={clipboard.id}
                    onClick={() => handleFolderClick(clipboard.id)}  
                    name={clipboard.name}
                    itemCount={clipboard.item_count || 0}
                    date={new Date(clipboard.created_at).toLocaleDateString('en-GB')}
                    onEdit={(e) => {
                        e.stopPropagation();
                        // Handle edit
                        console.log('Edit folder:', clipboard.id);
                    }}
                    onDelete={(e) => {
                        e.stopPropagation();
                        handleDeleteClipboard(clipboard.id);
                    }}
                />
              ))}
            </div>
          </div> */}

          <div className="p-6 grid justify-center [grid-template-columns:repeat(auto-fit,minmax(250px,300px))] gap-4 sm:gap-6">
            {folders.map((folder, index) => (
              <Folder
                key={folder.id}
                onClick={() => handleFolderClick(folder.id)}
                name={folder.name}
                itemCount={folder.item_count || 0}
                storageUsed={folder.storage_used || 0}
                retentionPeriod={folder.retention_period ?? null}
                date={new Date(folder.created_at).toLocaleDateString('en-GB')}
                onDelete={(e) => {
                  e.stopPropagation();
                  handleDeleteClipboard(folder.id);
                }}
                onRetentionEdit={(e) => {
                  e.stopPropagation();
                  setEditRetentionFolderId(folder.id);
                }}
              />
            ))}
          </div>

          {/* Items moved to Folder page - Dashboard focuses on folders only */}

      {/* Create Clipboard Modal */}
      <PopupContainer
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateRetention(DEFAULT_RETENTION);
          setCreateRetentionError(null);
          reset();
        }}
      >
        <div className="w-[320px] bg-white dark:bg-[#121212] border border-[#ECECEC] dark:border-[#202020] rounded-[32px] shadow-[0_26px_38px_0_rgba(18,18,18,0.05)] flex flex-col mx-4">
          {/* Top Section */}
          <div className="flex flex-col px-6 pt-6 pb-5 gap-4">
            <h2 className="font-medium text-xl leading-7 tracking-tight text-[#121212] dark:text-[#D0D0D0]">
              Create New Folder
            </h2>
            <form id="create-folder-form" onSubmit={handleSubmit(handleCreateClipboard)}>
              <Input
                placeholder="Enter folder name"
                error={errors.name?.message}
                {...register('name', { required: 'Folder name is required' })}
              />
            </form>
            {/* Retention selector */}
            <Select
              label="Retention"
              options={RETENTION_OPTIONS}
              value={createRetention}
              onChange={v => { setCreateRetention(v); setCreateRetentionError(null); }}
              error={createRetentionError}
            />
          </div>

          {/* Bottom Section */}
          <div className="flex items-center justify-between px-6 py-5 bg-[#FCFCFC] dark:bg-[#1A1A1A] border-t border-[#ECECEC] dark:border-[#202020] rounded-b-[32px]">
            <AccentButton
              variant="secondary"
              className="w-[130px]"
              onClick={() => {
                setShowCreateModal(false);
                reset();
              }}
            >
              Cancel
            </AccentButton>
            <AccentButton
              variant="primary"
              type="submit"
              form="create-folder-form"
              disabled={isCreating}
              className="w-[130px]"
            >
              {isCreating ? 'Creating...' : 'Create'}
            </AccentButton>
          </div>
        </div>
      </PopupContainer>

      {/* Delete Folder Confirmation Modal */}
      <PopupContainer
        isOpen={deleteFolderId !== null}
        onClose={() => setDeleteFolderId(null)}
      >
        <DeleteFolderConfirmation
          onClose={() => { setDeleteFolderId(null); setDeleteError(null); }}
          error={deleteError}
          isLoading={isDeleting}
          onConfirm={async () => {
            if (deleteFolderId === null) return;
            setIsDeleting(true);
            setDeleteError(null);
            try {
              await deleteFolder(deleteFolderId);
              setDeleteFolderId(null);
            } catch (err) {
              // Server returns domain error with message for non-empty folders
              const msg = err?.response?.data?.error?.message
                || err?.message
                || 'Failed to delete folder. Try again.';
              setDeleteError(msg);
            } finally {
              setIsDeleting(false);
            }
          }}
        />
      </PopupContainer>

      {/* Edit Folder Modal (rename + retention) */}
      <PopupContainer
        isOpen={editRetentionFolderId !== null}
        onClose={() => setEditRetentionFolderId(null)}
      >
        {editRetentionFolderId && (() => {
          const folder = folders.find(f => f.id === editRetentionFolderId);
          if (!folder) return null;
          return (
            <EditFolderModal
              folder={folder}
              onClose={() => setEditRetentionFolderId(null)}
              onSave={async ({ name, retentionPeriod }) => {
                await updateFolder({ folderId: editRetentionFolderId, name, retentionPeriod });
                setEditRetentionFolderId(null); // only closes on success
              }}
            />
          );
        })()}
      </PopupContainer>
    </DashboardLayout>
  );
};

export default Dashboard;
